<!-- Dashboard: key figures, value over time, allocation, results by period. -->
<script lang="ts">
  import * as E from "../core/engine";
  import * as X from "../core/analysis";
  import { app, need, PERIODS, PERIOD_NAMES, rangeOf } from "../lib/state.svelte";
  import { setPeriod } from "../lib/actions";
  import { esc, fmtDate, fmtNum, rangeLabel } from "../lib/format";
  import { baseOption, compactMoney, daySeries, itemTip, lineSeries, palette, series, signBar, timeAxis, tipMoney, tooltipRows, valueAxis } from "../lib/chart";
  import { groupBondRows } from "../lib/holdings";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const r = $derived(app.range);
  const st = $derived(E.periodStats(m, A, r.start, r.end));
  const e = $derived(st.e);
  const all = $derived(E.periodStats(m, A, E.addDays(m.firstDay, 1), m.today));
  const sess = $derived.by(() => {
    const [pe, le] = E.lastSession(m, e);
    let dayPL = A.V[le] - A.V[pe];
    for (let i = pe + 1; i <= le; i++) dayPL -= A.F[i];
    return { pe, le, dayPL };
  });
  const yr = $derived(m.today.slice(0, 4));
  const taxYear = $derived(A.years[+yr]?.taxDue || 0);
  const totalPL = $derived(A.V[e] - A.invested[e]);
  const pname = $derived(PERIOD_NAMES[app.period]);

  // reminder about IKE/IKZE limits in the last quarter
  const limitsLeft = $derived.by(() => {
    if (+m.today.slice(5, 7) < 9) return [];
    const cfgA = need(app.data).config;
    const rc = X.retirementContrib(m, cfgA);
    const lim = X.RETIREMENT_LIMITS[+yr] || (cfgA.limits as Record<string, typeof X.RETIREMENT_LIMITS[number]> | undefined)?.[yr];
    if (!lim) return [];
    const left: { k: string; rest: number; l: number }[] = [];
    for (const k of ["IKE", "IKZE"] as const) {
      if (!rc.accs[k].length) continue;
      const l = k === "IKE" ? lim.ike : cfgA.ikzeSelfEmployed !== false ? lim.ikzeSelf : lim.ikze;
      const rest = l - (rc.byYear[yr]?.[k]?.dep || 0);
      if (rest > 1) left.push({ k, rest, l });
    }
    return left;
  });

  // periods table (no duplicates with the same start)
  const periodRows = $derived.by(() => {
    const rows = PERIODS.filter((p) => p.k !== "CUSTOM").map((p) => {
      const rr = rangeOf(p.k);
      return { p, s: E.periodStats(m, A, rr.start, rr.end), rr };
    });
    const seen = new Set<string>();
    return rows.filter((x) => { const k = x.rr.start; if (seen.has(k) && x.p.k !== "MAX") return false; seen.add(k); return true; });
  });

  const hold = $derived(E.holdings(m, app.accList, r.start, r.end));
  const alloc = $derived.by(() => {
    const al = E.allocation(m, hold, A, e, "group");
    return { al, tot: al.reduce((s, x) => s + x.value, 0) };
  });

  const valueOpt = $derived.by(() => {
    const p = palette();
    const s0 = st.s0;
    const ser = [
      lineSeries("Wartość portfela", daySeries(m, A.V, s0, e), series(0), { areaStyle: { color: series(0), opacity: 0.08 } }),
      lineSeries("Wpłacony kapitał", daySeries(m, A.invested, s0, e), p.muted, { lineStyle: { width: 2, color: p.muted, type: "solid" }, step: "end" }),
    ];
    if (app.tax) ser.push(lineSeries("Wartość po podatku", daySeries(m, A.V, s0, e, (v, i) => v - A.L[i]), series(1)));
    return {
      ...baseOption(p), xAxis: timeAxis(p), yAxis: valueAxis(compactMoney, p, { scale: true }), series: ser,
      tooltip: { ...baseOption(p).tooltip, formatter: (ps: unknown) => tooltipRows(ps, (v) => tipMoney(v)) },
    };
  });
  const instrOpt = $derived.by(() => {
    const p = palette();
    const hs = groupBondRows(hold).filter((h) => Math.abs(h.plPeriod) > 0.5).sort((a, b) => b.plPeriod - a.plPeriod);
    return {
      ...baseOption(p), grid: { left: 24, right: 30, top: 8, bottom: 6, containLabel: true },
      tooltip: itemTip(p, (x) => `${esc(x.name)}<br><b>${tipMoney(x.value)}</b>`),
      xAxis: valueAxis(compactMoney, p, { splitNumber: 4, axisLabel: { color: p.muted, formatter: compactMoney, hideOverlap: true } }),
      yAxis: { type: "category", inverse: true, data: hs.map((h) => h.name), axisLabel: { color: p.ink2, width: 220, overflow: "truncate" }, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } } },
      series: [{ type: "bar", barMaxWidth: 18, data: hs.map((h) => signBar(h.plPeriod, p, true)) }],
    };
  });
</script>

<ViewHead title="Pulpit" sub="{rangeLabel(r)} · {app.allAccounts ? 'wszystkie rachunki' : app.accList.join(', ')}" />
<div class="kpis">
  <Kpi label="Wartość portfela">
    {#snippet value()}<Money v={A.V[e]} />{/snippet}
    {#snippet sub()}<Signed v={sess.dayPL} /> <Pct v={A.V[sess.pe] > 0 ? sess.dayPL / A.V[sess.pe] : null} /> · sesja {fmtDate(m.days[sess.le]).slice(0, 5)}{/snippet}
  </Kpi>
  <Kpi label="Wpłacony kapitał (netto)" sub="wpłaty − wypłaty">{#snippet value()}<Money v={A.invested[e]} />{/snippet}</Kpi>
  <Kpi label="Zysk całkowity">
    {#snippet value()}<Signed v={totalPL} />{/snippet}
    {#snippet sub()}<Pct v={A.invested[e] > 0 ? totalPL / A.invested[e] : null} /> wpłaconego kapitału · XIRR <Pct v={all.xirr} />/rok{/snippet}
  </Kpi>
  <Kpi label="Wynik: {pname}">
    {#snippet value()}<Signed v={st.gross} />{/snippet}
    {#snippet sub()}TWR <Pct v={st.twr} />{#if st.xirr !== null && st.days >= 365}{" "}· XIRR <Pct v={st.xirr} />/rok{/if}{/snippet}
  </Kpi>
  {#if app.tax}
    <Kpi label="Wynik po podatku: {pname}">
      {#snippet value()}<Signed v={st.net} />{/snippet}
      {#snippet sub()}TWR po podatku <Pct v={st.twrNet} />{/snippet}
    </Kpi>
    <Kpi label="Podatek Belki">
      {#snippet value()}<Money v={taxYear} />{/snippet}
      {#snippet sub()}należny za {yr} · odroczony <Money v={A.latent[e]} />{/snippet}
    </Kpi>
  {:else}
    <Kpi label="Zysk niezrealizowany" sub="wartość papierów − koszt nabycia">{#snippet value()}<Signed v={st.unrealized} />{/snippet}</Kpi>
  {/if}
</div>

{#if limitsLeft.length}
  <div class="notice" style="margin-bottom:14px">Do końca roku: {#each limitsLeft as x, i (x.k)}{#if i}{" · "}{/if}{x.k}: zostało <b><Money v={x.rest} /></b> z limitu {fmtNum(x.l, 2)} zł{/each}. <a href="#/ike">Szczegóły →</a></div>
{/if}

<div class="grid g-2-1">
  <Card title="Wartość portfela i wpłacony kapitał"><Chart id="chValue" class="tall" option={valueOpt} /></Card>
  <Card title="Alokacja">
    {#snippet extra()}<a class="small" href="#/allocation">szczegóły →</a>{/snippet}
    <div id="allocList" class="legend-list">
      {#each alloc.al as x, k (x.name)}
        <div>
          <div class="legend-row"><span><span class="dot" style="background:{series(k)}"></span>{x.name}</span><span class="money">{fmtNum(x.value)} zł</span><b>{fmtNum((x.value / alloc.tot) * 100, 1)}%</b></div>
          <div class="meter"><div style="width:{(x.value / alloc.tot) * 100}%;background:{series(k)}"></div></div>
        </div>
      {/each}
    </div>
  </Card>
</div>

<Card title="Wyniki w różnych okresach" hint="TWR — stopa zwrotu ważona czasem (niezależna od wpłat) · XIRR — roczna stopa zwrotu z Twoich wpłat (dla okresów ≥ 1 roku) · kliknij wiersz, aby wybrać okres">
  <div class="table-wrap"><table><thead><tr><th class="l">Okres</th><th class="l">Od</th><th>Wynik</th><th>TWR</th><th>TWR rocznie</th><th>XIRR (roczna)</th><th>Inflacja</th><th>TWR realnie</th>
    {#if app.tax}<th>Wynik po podatku</th><th>TWR po podatku</th>{/if}<th>Wpłaty netto</th></tr></thead>
    <tbody>
      {#each periodRows as { p, s, rr } (p.k)}
        <tr class="clickable" data-p={p.k} onclick={() => setPeriod(p.k)}>
          <td class="l"><b>{p.label}</b> <span class="muted">{PERIOD_NAMES[p.k]}</span></td><td class="l">{fmtDate(rr.start)}</td>
          <td><Signed v={s.gross} /></td><td><Pct v={s.twr} /></td><td><Pct v={s.twrAnn} /></td><td>{#if s.days >= 365}<Pct v={s.xirr} />{:else}–{/if}</td>
          <td>{#if m.cpi}<Pct v={E.inflationBetween(m, s.s0, s.e)} d={1} sign={false} />{:else}–{/if}</td>
          <td>{#if m.cpi}<Pct v={(1 + (app.tax ? s.twrNet : s.twr)) / (1 + (E.inflationBetween(m, s.s0, s.e) as number)) - 1} />{:else}–{/if}</td>
          {#if app.tax}<td><Signed v={s.net} /></td><td><Pct v={s.twrNet} /></td>{/if}<td><Money v={s.flows} /></td>
        </tr>
      {/each}
    </tbody>
  </table></div>
</Card>
<Card title="Wynik instrumentów: {pname}" hint="zmiana wartości w okresie po uwzględnieniu kupna i sprzedaży"><Chart id="chInstr" class="short" option={instrOpt} /></Card>
